import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Save, Plus, X, Users, ShieldCheck, Archive, Building2, BadgeCheck } from "lucide-react";

const ListEditor = ({ title, items, onChange, canEdit, testid, placeholder }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) return toast.error(`«${v}» υπάρχει ήδη`);
    onChange([...items, v]);
    setDraft("");
  };
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={testid}>
      <h3 className="font-medium text-[#45423C]">{title}</h3>
      <div className="mt-4 flex flex-wrap gap-2">
        {items.length === 0 && <span className="text-sm text-[#8A8577]">Κενή λίστα</span>}
        {items.map((it) => (
          <span key={it} data-testid={`${testid}-item-${it}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#F1EDE6] pl-3 pr-1.5 py-1.5 text-sm text-[#45423C]">
            {it}
            {canEdit && (
              <button data-testid={`${testid}-remove-${it}`} onClick={() => onChange(items.filter((x) => x !== it))}
                className="rounded-full p-0.5 text-[#8A8577] hover:bg-white hover:text-[#991B1B]">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        ))}
      </div>
      {canEdit && (
        <div className="mt-4 flex gap-2">
          <input data-testid={`${testid}-input`} value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder={placeholder}
            className="flex-1 rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]" />
          <button data-testid={`${testid}-add`} onClick={add}
            className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A]">
            <Plus className="h-4 w-4" /> Προσθήκη
          </button>
        </div>
      )}
    </div>
  );
};

const CleanupRow = ({ row, categories, canEdit, onDone }) => {
  const [target, setTarget] = useState("");
  const mv = useMutation({
    mutationFn: async () => (await api.post("/settings/org/category-remap", { source: row.category, target })).data,
    onSuccess: (d) => { toast.success(`${d.updated} είδη μεταφέρθηκαν σε «${target}»`); onDone(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const key = row.category || "none";
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid={`cleanup-row-${key}`}>
      <span className="font-medium text-[#45423C] min-w-[180px]">{row.category || <em className="text-[#8A8577]">χωρίς κατηγορία</em>}</span>
      <span className="rounded-full bg-[#F1EDE6] px-2 py-0.5 text-xs text-[#6F6A60]" data-testid={`cleanup-count-${key}`}>{row.count} είδη</span>
      {canEdit && <>
        <select data-testid={`cleanup-target-${key}`} value={target} onChange={(e) => setTarget(e.target.value)}
          className="rounded-lg border border-[#E3DED4] bg-white px-2 py-1.5 text-xs outline-none focus:border-[#45423C]">
          <option value="">Μεταφορά σε…</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button data-testid={`cleanup-apply-${key}`} disabled={!target || mv.isPending} onClick={() => mv.mutate()}
          className="rounded-lg bg-[#6E7A63] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Μεταφορά</button>
      </>}
    </div>
  );
};

const CategoryCleanup = ({ categories, canEdit }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["category-cleanup"], queryFn: async () => (await api.get("/settings/org/category-cleanup")).data });
  const rows = data?.rows || [];
  const total = rows.reduce((s, r) => s + r.count, 0);
  const done = () => ["category-cleanup", "catalog", "ingredients"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="settings-category-cleanup">
      <h3 className="font-medium text-[#45423C]">Καθαρισμός κατηγοριών ειδών</h3>
      <p className="text-xs text-[#6F6A60] mt-1" data-testid="cleanup-total">
        {total === 0 ? "Όλα τα είδη έχουν κατηγορία από τη λίστα." : `${total} είδη έχουν κατηγορία εκτός λίστας (ή καμία). Μετάφερέ τα μαζικά σε σωστή κατηγορία — αποθήκευσε πρώτα τη λίστα αν πρόσθεσες νέες.`}
      </p>
      {rows.length > 0 && <div className="mt-4 space-y-2">{rows.map((r) => <CleanupRow key={r.category || "none"} row={r} categories={categories} canEdit={canEdit} onDone={done} />)}</div>}
    </div>
  );
};


export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);
  const [itemUnits, setItemUnits] = useState([]);
  const [leaveDays, setLeaveDays] = useState(20);
  const [orderThreshold, setOrderThreshold] = useState(500);

  const { data } = useQuery({ queryKey: ["org-settings"], queryFn: async () => (await api.get("/settings/org")).data });
  useEffect(() => {
    if (data) {
      setDepartments(data.departments || []);
      setPositions(data.positions || []);
      setItemCategories(data.item_categories || []);
      setItemUnits(data.item_units || []);
      setLeaveDays(data.annual_leave_days ?? 20);
      setOrderThreshold(data.order_approval_threshold ?? 500);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/settings/org", { departments, positions, item_categories: itemCategories, item_units: itemUnits, annual_leave_days: Number(leaveDays) || 0, order_approval_threshold: Number(orderThreshold) || 0 })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["org-settings"] }); toast.success("Οι ρυθμίσεις αποθηκεύτηκαν"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const links = [
    { to: "/employees", label: "Υπάλληλοι", desc: "Κατάλογος, δημιουργία και διαγραφή υπαλλήλων", icon: Users, roles: ["owner", "hr", "manager"] },
    { to: "/users", label: "Χρήστες & Πρόσβαση", desc: "Λογαριασμοί σύνδεσης και δικαιώματα", icon: ShieldCheck, roles: ["owner", "manager"] },
    { to: "/trash", label: "Κάδος Ανακύκλωσης", desc: "Επαναφορά διαγραμμένων εγγραφών", icon: Archive, roles: ["owner", "hr"] },
  ].filter((l) => l.roles.includes(user?.role));

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Οργάνωση</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Ρυθμίσεις</h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            Τμήματα και ρόλοι που εμφανίζονται ως επιλογές όταν δημιουργείτε ή επεξεργάζεστε υπάλληλο.
          </p>
        </div>
        {canEdit && (
          <button data-testid="settings-save" onClick={() => save.mutate()} disabled={save.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
            <Save className="h-4 w-4" /> {save.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ListEditor title="Τμήματα" items={departments} onChange={setDepartments} canEdit={canEdit}
          testid="settings-departments" placeholder="π.χ. Housekeeping" />
        <ListEditor title="Ρόλοι / Θέσεις" items={positions} onChange={setPositions} canEdit={canEdit}
          testid="settings-positions" placeholder="π.χ. Καθαριότητα" />
        <ListEditor title="Κατηγορίες ειδών (κατάλογος)" items={itemCategories} onChange={setItemCategories} canEdit={canEdit}
          testid="settings-item-categories" placeholder="π.χ. Λαχανικά" />
        <ListEditor title="Μονάδες μέτρησης" items={itemUnits} onChange={setItemUnits} canEdit={canEdit}
          testid="settings-item-units" placeholder="π.χ. kg, τεμ, κιβ" />
      </div>
      <CategoryCleanup categories={data?.item_categories || []} canEdit={canEdit} />

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="settings-leave">
        <h3 className="font-medium text-[#45423C]">Άδειες</h3>
        <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold block mt-4">
          Ετήσιο δικαίωμα άδειας (ημέρες)
        </label>
        <input data-testid="settings-leave-days" type="number" min="0" value={leaveDays} disabled={!canEdit}
          onChange={(e) => setLeaveDays(e.target.value)}
          className="mt-1 w-40 rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1]" />
        <p className="text-xs text-[#8A8577] mt-2">Χρησιμοποιείται για το υπόλοιπο αδειών στον πίνακα του υπαλλήλου.</p>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="settings-orders">
        <h3 className="font-medium text-[#45423C]">Παραγγελίες</h3>
        <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold block mt-4">
          Όριο έγκρισης παραγγελίας (€)
        </label>
        <input data-testid="settings-order-threshold" type="number" min="0" value={orderThreshold} disabled={!canEdit}
          onChange={(e) => setOrderThreshold(e.target.value)}
          className="mt-1 w-40 rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1]" />
        <p className="text-xs text-[#8A8577] mt-2">Παραγγελίες πάνω από αυτό το ποσό απαιτούν έγκριση από Ιδιοκτήτη/Διευθυντή/HR πριν σταλούν στον προμηθευτή.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {links.map((l) => (
          <Link key={l.to} to={l.to} data-testid={`settings-link-${l.to.slice(1)}`}
            className="rounded-2xl bg-white border border-[#E3DED4] p-5 hover:border-[#B9AE9A] transition-colors">
            <div className="flex items-center gap-2 text-[#45423C]">
              <l.icon className="h-4 w-4 text-[#C86235]" />
              <span className="font-medium">{l.label}</span>
            </div>
            <p className="text-xs text-[#6F6A60] mt-1.5">{l.desc}</p>
          </Link>
        ))}
      </div>

      <div className="rounded-2xl border border-dashed border-[#E3DED4] p-5 text-sm text-[#8A8577] flex items-center gap-2">
        <Building2 className="h-4 w-4" />
        Εδώ θα προστεθούν και άλλες ρυθμίσεις (στοιχεία εταιρείας, ειδοποιήσεις, cost centers).
        <BadgeCheck className="h-4 w-4 ml-auto text-[#6E7A63]" />
      </div>
      <VersionFooter />
    </div>
  );
}

function VersionFooter() {
  const { data: v } = useQuery({ queryKey: ["app-version"], queryFn: async () => (await api.get("/version")).data });
  if (!v) return null;
  return (
    <p className="text-[11px] font-mono text-[#8A8577] text-right" data-testid="app-version">
      Έκδοση {v.sha || "—"} ({v.branch || "?"}) · build {v.date ? v.date.slice(0, 16).replace("T", " ") : "—"} · εκκίνηση {v.started_at?.slice(0, 16).replace("T", " ")}
      {v.test_writes_allowed && <span className="ml-2 text-[#92400E]">· TEST ENV</span>}
    </p>
  );
}
